// Description: This is the main component that renders the chat interface. 
// It contains the chat entries, user input, and buttons to interact with the AI bot. The component also handles file uploads and the display of generated AI responses.

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./styles.css";
import { FaPaperclip } from "react-icons/fa";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWindowMaximize, faPlay } from '@fortawesome/free-solid-svg-icons';

// 1. Create a custom hook to display an ellipsis animation
function useEllipsis(delay = 500, maxDots = 3) {
  const [dots, setDots] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prevDots) => (prevDots.length >= maxDots ? "" : prevDots + "."));
    }, delay);

    return () => clearInterval(interval);
  }, [delay, maxDots]);

  return dots;
}

// 2. Create a ChatComponent function that renders the chat interface
const ChatComponent = () => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [userMessage, setUserMessage] = useState("");
  const [chatEntries, setChatEntries] = useState([
    { type: "AI", content: <span>Hi! I'm an AI ChatBot, how can I help with regards to Tengah Centralised Cooling System (CCS)?<br/><em className="ai-disclaimer-text">( As an AI ChatBot, if I miss something, ask away! )</em></span> }
  ]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [, setIsUploading] = useState(false);
  const dots = useEllipsis();
  const chatEndRef = useRef(null);
  const isStoppedRef = useRef(false);
  const intervalRef = useRef(null);
  const [isChatVisible, setChatVisible] = useState(true);

  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(scrollToBottom, [chatEntries]);

  const handleMinimize = () => {
    setIsMinimized(true);
    setIsMaximized(false);
    window.parent.postMessage('minimizeChatbox', '*');
  };

  const handleMaximize = () => {
    setIsMaximized(true);
    setIsMinimized(false);
    window.parent.postMessage('maximizeChatbox', '*');
  };

  const handleClose = () => {
    setChatEntries([]);
    setIsMinimized(false);
    setIsMaximized(false);
    setChatVisible(false);
    setIsGenerating(false);
    setUserMessage("");
    setUploadedFile(null);
    setUploadedFileName("");
    setIsUploading(false);

    if (intervalRef.current) {
      intervalRef.current.cancel("Chatbot closed by user.");
      intervalRef.current = null;
    }

    window.parent.postMessage('closeChatbox', '*');
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    setUploadedFile(file);
    setUploadedFileName(file.name);
    setUserMessage(`Selected File to Upload: [ ${file.name} ]`);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setUserMessage("");

    if ((typeof userMessage !== "string" || !userMessage.trim()) && !uploadedFile) {
      return;
    }

    if (uploadedFile) {
      setIsUploading(true);
      setChatEntries((prevChatEntries) => [
        ...prevChatEntries,
        { type: "User", content: `Uploading File: [ ${uploadedFileName} ]` },
      ]);

      try {
        await axios.put(`${process.env.REACT_APP_API_URL}/upload`, uploadedFile, {
          headers: { "Content-Type": uploadedFile.type },
        });

        setChatEntries((prevChatEntries) => [
          ...prevChatEntries,
          { type: "User", content: `[ ${uploadedFileName} ] Uploaded !` },
        ]);

        setUploadedFile(null);
        setUploadedFileName("");
        setIsUploading(false);
      } catch (error) {
        console.error("Error uploading file:", error);
      }

      setUserMessage("");
      return;
    }

    setChatEntries([...chatEntries, { type: "User", content: userMessage }]);
    setIsGenerating(true);
    isStoppedRef.current = false;

    const source = axios.CancelToken.source();

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}`,
        { content: userMessage },
        { cancelToken: source.token }
      );

      if (!isStoppedRef.current) {
        setChatEntries((prevChatEntries) => [
          ...prevChatEntries,
          {
            type: "AI",
            content: response.data.response,
            contentType: response.data.type,
          },
        ]);
        setIsGenerating(false);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else {
        console.error("Error generating AI response:", error);
      }
    } finally {
      setUserMessage("");
      intervalRef.current = source;
    }
  };

  const handleStop = async () => {
    isStoppedRef.current = true;
    setIsGenerating(false);
    setChatEntries((prevChatEntries) => [
      ...prevChatEntries,
      { type: "AI", content: "Okay! Your query has been cancelled." },
    ]);

    await axios.post(`${process.env.REACT_APP_API_URL}/kill`);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  // A footer component - contains the link to view past conversation logs stored in AWS S3.
  const footer = (
    <div className="footer">
      {/* <a href="http://127.0.0.1:8080/" target="_blank" rel="noopener noreferrer" className="logs-link" style={{ float: 'right' }}>
        View Conversation Logs
      </a>  <br/> */}
      Containerized AI ChatBot developed by KaiJian (v14.3)
    </div>
  );

  return (
    <div className={`parent-container ${isMinimized ? 'minimized' : ''} ${isChatVisible ? '' : 'closed'}`}>
      { isChatVisible && (
  
        <div className={`chat-wrapper ${isMinimized ? 'minimized' : ''} ${isMaximized ? 'maximized' : ''}`}>
          <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>  
            <button className="minimize-button hidden" onClick={handleMinimize}>&ndash;</button>
            <button className="maximize-button hidden" onClick={handleMaximize}>
              <FontAwesomeIcon icon={faWindowMaximize} />
            </button>
            <button className="close-button hidden" onClick={handleClose}>&times;</button>
          </div>
          
          <div className="logo-text-container header-container">
          <div className="welcome-message">CloudRGB AI Chatbot</div>
            <img src="/CloudRGB-Circular-Logo.png" className="circular-logo" alt="Logo" />
          </div>

          {!isMinimized && (
            <div>
              <div className="chat-wrapper"> 
                <div className="chat-component">
                  <div>
                    <div className="chat-container" id="chatEntries">
                    {chatEntries.map((entry, index) => (
                              <div key={index} className={`chat-entry ${entry.type}`}>
                                <img 
                              src={entry.type === "AI" ? "/CloudRGB-AI-Logo.png" : "/User-Circular-Logo.png"}
                              alt={entry.type} 
                              className={`chat-logo ${entry.type.toLowerCase()}-logo`} 
                            />
                                <div className="message-content">
                                  {entry.contentType === "image" ? (
                                    <>
                                      <img src={`data:image/png;base64,${entry.content}`} alt="Generated by AI"/>
                                      <a href={`data:image/png;base64,${entry.content}`} download="image.png"> Download Image</a>
                                    </>
                                  ) : (
                                    entry.content
                                  )}
                                </div>
                              </div>
                            ))}
                      {isGenerating && (
                            <div className="chat-entry AI generating">
                              <img 
                                src="/CloudRGB-Circular-Logo.png" // Replace with AI logo
                                alt="AI" 
                                className="chat-logo ai-logo" 
                              />
                              <div className="message-content">
                                {dots}
                              </div>
                            </div>
                          )}
                      <div ref={chatEndRef} />
                    </div>
                    
                    <form id="chatForm" onSubmit={handleSubmit}>
                      <textarea 
                        id="contentInput" 
                        name="content" 
                        placeholder="Ask anything..." 
                        value={userMessage} 
                        onChange={(e) => setUserMessage(e.target.value)} 
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSubmit(e);
                          }
                        }}
                      > 
                      </textarea>
                      
                      <div className="button-container">
                        <button type="button" onClick={handleSubmit} className="play-button">
                          <FontAwesomeIcon icon={faPlay} />
                        </button>
                        <button type="button" id="stop" onClick={handleStop} disabled={!isGenerating}>
                          <span></span><span></span>
                        </button>
                        <label htmlFor="file-upload" className="file-upload-label"><FaPaperclip/></label>
                        <input id="file-upload" type="file" style={{ display: 'none' }} onChange={handleFileUpload}/>
                      </div>
                    </form>
                  </div>
                  {footer}
                </div>  
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatComponent;